import { useEffect, useRef } from 'react';
import type { OutreachCandidate } from '../../types/taOutreach';
import { groupByPerson, preferOwn } from './onePerPerson';

/** Accepted, no draft yet: what Write & send drafts by itself, or asks to read first (Oct 28 2026). */
const WAITING = new Set(['imported', 'analysed', 'queued']);
const hasProfile = (c: OutreachCandidate) => Boolean(`${c.profileText || ''}`.trim());

/** How many drafts the auto-draft writes at once (Sofia, Nov 2 2026), as Update all does. */
export const AUTO_LANES = 3;

/**
 * Everyone accepted without a draft, one row per person (nobody whose other row already has a
 * draft): `toRead` still needs the extension, `toDraft` has its profile. A row whose profile is
 * read wins over the viewer's own unread one (CodeRabbit, PR #753) — a person a colleague already
 * read is drafted, not sent to "profile not read" — then the viewer's own row.
 */
export function waitingPeople(items: OutreachCandidate[], viewer: string) {
  const waiting = groupByPerson(items.filter((c) => c.status !== 'dropped' && c.status !== 'shortlisted'))
    .filter((rows) => !rows.some((c) => c.draft || !WAITING.has(c.status)))
    .map((rows) => preferOwn(rows.some(hasProfile) ? rows.filter(hasProfile) : rows, viewer));
  return { toRead: waiting.filter((c) => !hasProfile(c)), toDraft: waiting.filter(hasProfile) };
}

/**
 * Write & send drafts everyone accepted whose profile is read, by itself (Sofia, Oct 28 2026):
 * before, a broken Accept (no extension, page closed, no brief yet) left them waiting for a click
 * on "Write their InMails". Once per row per page.
 *
 * **Its own lanes, 3 at once** (Sofia, Nov 2 2026: "it takes far too long"): it no longer waits for
 * the page's `sequential` (`busyLoop`), so Approve N, Start or Update all are never blocked by a
 * batch of drafts, and a batch is never held up by them. A row that comes in while drafts run
 * (accepted meanwhile) joins the queue and starts as soon as a lane is free. Leaving the steps
 * where it runs (`active` false) drops what has not started yet; it is asked again on return.
 * `draft` = queue (when not yet) and draft with `auto` (a clean draft comes back approved). The
 * proxy's claim on `/:id/draft` keeps two recruiters' pages from writing the same InMail twice.
 */
export function useAutoDraft({
  toDraft,
  active,
  draft,
  lanes = AUTO_LANES,
}: {
  toDraft: OutreachCandidate[];
  active: boolean;
  /** Queue (when not yet) and draft one row. */
  draft: (id: string, queued: boolean) => Promise<void>;
  lanes?: number;
}) {
  const tried = useRef(new Set<string>());
  const waiting = useRef<{ id: string; queued: boolean }[]>([]);
  const running = useRef(0);
  // The latest `draft` (a new function each render), read by lanes that started earlier.
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  });
  // What to draft, as a string: the effect runs when that set changes, not on every render.
  const key = active ? toDraft.map((c) => `${c.id}|${c.status === 'queued' ? 'q' : ''}`).join(',') : '';
  useEffect(() => {
    if (!key) {
      // Not here any more: what has not started is asked again on return.
      waiting.current.forEach((r) => tried.current.delete(r.id));
      waiting.current = [];
      return;
    }
    for (const [id, q] of key.split(',').map((x) => x.split('|'))) {
      if (tried.current.has(id)) continue;
      tried.current.add(id);
      waiting.current.push({ id, queued: q === 'q' });
    }
    const pump = () => {
      while (running.current < lanes && waiting.current.length) {
        const next = waiting.current.shift() as { id: string; queued: boolean };
        running.current += 1;
        void draftRef.current(next.id, next.queued)
          .catch(() => undefined)
          .finally(() => {
            running.current -= 1;
            pump();
          });
      }
    };
    pump();
  }, [key, lanes]);
}
